import { CSSObject } from 'styled-components';
import Select from 'react-select';
import React, { FC, useMemo, useRef } from 'react';
import IconCross from '@shared/icons/IconCross';
import FormFieldWrapper from '@shared/form/FormFieldWrapper';
import CreatableSelect from 'react-select/creatable';

interface IStyleObject {
  [key: string]: any;
}

const createStyles = (needHighlightFirstOption?: boolean): IStyleObject => {
  return {
    control: (provided: any, state: any) => {
      return {
        ...provided,
        backgroundColor: '#211F24',
        minHeight: state.selectProps.isFilter ? '42px' : '30px',
        borderColor: 'none',
        border: 'none',
        color: 'white',
        boxShadow: 'none',
        width: '100%',
        cursor: 'pointer',
      };
    },
    dropdownIndicator: (provided: any, state: any) => ({
      ...provided,
      padding: 5,
    }),
    clearIndicator(base: CSSObject): CSSObject {
      base.padding = 0;
      base.display = 'none';
      // иконка отмены всех опшенов
      return base;
    },
    indicatorsContainer(base: CSSObject): CSSObject {
      base.padding = 10;
      base.display = 'none';
      // область открытия дропдауна

      return base;
    },
    placeholder: (base: CSSObject): CSSObject => {
      return {
        ...base,
        color: '#ffffff',
        fontSize: '12px',
      };
    },
    noOptionsMessage: (base: CSSObject): CSSObject => {
      return {
        ...base,
        backgroundColor: '#252328',
        color: 'white',
      };
    },
    menu: (base: CSSObject): CSSObject => {
      return {
        ...base,
        backgroundColor: '#252328',
        color: 'white',
        border: '1px solid #211F24',
        borderRadius: '10px',
        overflow: 'hidden',
        width: '200px',
      };
    },
    indicatorSeparator(base: CSSObject): CSSObject {
      base.padding = 0;
      base.display = 'none';
      // vertical line between icons in input

      return base;
    },
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: '#252328',
      color: 'white',
      cursor: 'pointer',
      width: '200px',
      '&:first-child': {
        color: needHighlightFirstOption ? '#D7F525' : 'white',
      },
    }),
    singleValue: (provided: any, state: any) => ({
      ...provided,
      color: 'white',
    }),

    input: (provided: any, state: any) => ({
      ...provided,
      input: {
        margin: '0px',
        color: 'white',
      },
      height: '29px',
      fontSize: '12px',
      paddingTop: '7px',
      width: '100%',
      color: 'white',
      paddingBottom: '7px',
      marginTop: 0,
      marginBottom: 0,
    }),
  };
};

interface ICustomSelectProps {
  options: any;
  placeholder: string;
  isLoading: boolean;
  width?: number | string;
  value?: ISelectOption | null;
  needHighlightFirstOption?: boolean;
  onChange: (val: ISelectOption | null) => void;
  state: any;
  disable?: boolean;
  isCreatable?: boolean;
}

export interface ISelectOption {
  value: string;
  label: string;
}

const SingleSelectForm: FC<ICustomSelectProps> = ({
  options,
  placeholder,
  isLoading = false,
  width = 150,
  needHighlightFirstOption = false,
  value,
  onChange,
  state,
  disable = false,
  isCreatable = false,
}) => {
  const selectRef = useRef(null);

  const styles = useMemo(() => {
    return createStyles(needHighlightFirstOption);
  }, [needHighlightFirstOption]);

  return (
    <FormFieldWrapper state={state}>
      <div
        style={{
          minWidth: `${Number.isInteger(width) ? width + 'px' : width}`,
          maxWidth: `${Number.isInteger(width) ? width + 'px' : width}`,
        }}
        className={`rounded-[8px] bg-[#211F24] text-[white] cursor-pointer relative text-[12px] h-[33px]`}
      >
        {isCreatable ? (
          <CreatableSelect
            ref={selectRef}
            options={options}
            menuPosition="fixed"
            isMulti={false}
            className="h-[33px]"
            openMenuOnFocus={false}
            hideSelectedOptions={true}
            closeMenuOnSelect={true}
            styles={styles}
            isClearable={true}
            isLoading={isLoading}
            placeholder={placeholder}
            isDisabled={disable}
            formatCreateLabel={inputValue => `Создать "${inputValue}"`}
            onChange={(options: any) => {
              if (onChange) {
                onChange(options);
              }
            }}
            value={value}
          />
        ) : (
          <Select
            ref={selectRef}
            options={options}
            menuPosition="fixed"
            isMulti={false}
            className="h-[33px]"
            openMenuOnFocus={false}
            hideSelectedOptions={true}
            closeMenuOnSelect={true}
            styles={styles}
            isClearable={true}
            isLoading={isLoading}
            placeholder={placeholder}
            isDisabled={disable}
            onChange={(options: any) => {
              if (onChange) {
                onChange(options);
              }
            }}
            value={value}
          />
        )}
        {!value ? (
          <>
            <svg
              onClick={() => {
                {
                  /* @ts-ignore */
                }
                return selectRef.current.focus();
              }}
              className="absolute right-[10px] top-[14px]"
              width="7"
              height="5"
              viewBox="0 0 7 5"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.71098 1.98938L4.18608 4.24524C3.80588 4.58492 3.19172 4.58492 2.81152 4.24524L0.286622 1.98939C-0.327542 1.44066 0.111146 0.500001 0.978776 0.500001L6.02857 0.5C6.8962 0.5 7.32514 1.44066 6.71098 1.98938Z"
                fill="white"
              />
            </svg>
          </>
        ) : !disable ? (
          <div
            className="absolute right-[5px] top-[8px] bg-[#211F24] py-[5px] pl-[5px]"
            onClick={() => {
              if (onChange) {
                onChange(null);
              }
            }}
          >
            <IconCross width={8} height={8} />
          </div>
        ) : null}
      </div>
    </FormFieldWrapper>
  );
};

export default SingleSelectForm;
